import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Card, Alert, Typography, Image, Row, Col, Button, Modal, message, Empty, Dropdown, Menu, Upload, List, Input, Tag, Badge, Collapse, Drawer, Divider   } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, UploadOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, SendOutlined, MoreOutlined, MessageOutlined, CommentOutlined, MessageFilled  } from "@ant-design/icons";
import axios from '../services/axios';
import ReactFlow, { MiniMap, Controls, Background, applyNodeChanges, applyEdgeChanges } from "reactflow";
import { ENGINEER } from "../constants/role";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const DocumentDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState(location.state);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingDocument, setFetchingDocument] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [revisionModalVisible, setRevisionModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRevisionUrl, setSelectedRevisionUrl] = useState(null);
  const [selectedRevisionIndex, setSelectedRevisionIndex] = useState(0);
  const [responseDocuments, setResponseDocuments] = useState([]);
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const projectName = JSON.parse(localStorage.getItem('Project'));
  const user = localStorage.getItem('user');
  const role = JSON.parse(localStorage.getItem('role'))[0];
  const userType = localStorage.getItem('userType');
  const [timelineData, setTimelineData] = useState([]);
  const [engCommentModalVisible, setEngCommentModalVisible] = useState(false);
  const [engineerComment, setEngineerComment] = useState('');
  const [title, setTitle] = useState('');
  const [chatVisible, setChatVisible] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [baseDocument, setBaseDocument] = useState([])
  const [baseDocModalOpen, setBaseDocModalOpen] = useState(false);
  const [baseDocUrl, setBaseDocUrl] = useState(null);

  // Get status color based on action type
  const getStatusColor = (actionType, status) => {
    switch (actionType) {
      case 'Uploaded':
        return 'blue';
      case 'Sent':
        return 'orange';
      case 'Approved':
        return 'green';
      case 'Rejected':
        return 'red';
      case 'Resubmitted':
        return 'purple';
      default:
        return 'default';
    }
  };

  // Get status icon based on action type
  const getStatusIcon = (actionType) => {
    switch (actionType) {
      case 'Uploaded':
        return <UploadOutlined />;
      case 'Sent':
        return <SendOutlined />;
      case 'Approved':
        return <CheckCircleOutlined />;
      case 'Rejected':
        return <CloseCircleOutlined />;
      case 'Resubmitted':
        return <UploadOutlined />;
      default:
        return null;
    }
  };

  const fetchUnreadCount = async () => {
    if (!documentData?._id) return;

    try {
      const res = await axios.post("/get-unread-count", {
        documentId: documentData._id,
        userId: user
      });

      setUnreadCount(res.data?.unreadCount || 0);
    } catch (err) {
      console.error("Failed to fetch unread count", err);
    }
  };

  useEffect(() => {
    if (documentData?._id) {
      fetchUnreadCount();
    }
  }, [documentData?._id]);

  const fetchResponseDocuments = async (docRef) => {
    try {
      const res = await axios.post("/fetch-response-document", {
        projectName,
        documentReference: docRef
      });

      setResponseDocuments(res.data?.document?.responses || []);
    } catch (err) {
      console.error("Failed to fetch response documents", err);
      setResponseDocuments([]);
    }
  };

  const getBaseDocument = async (docRef) => {
    try {
      const res = await axios.post("/get-base-document", {
        projectName,
        respondingTo: documentData.respondingTo
      });

      if(res?.data?.document){
        setBaseDocument(res.data.document);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong1');
      }   
    }
  };

  useEffect(() => {
    if (documentData?.documentReference && !documentData?.respondingTo) {
      fetchResponseDocuments(documentData.documentReference);
    }
  }, [documentData?.documentReference]);

   useEffect(() => {
    if (documentData?.documentReference && documentData?.respondingTo) {
      getBaseDocument(documentData.documentReference);
    }
  }, [documentData?.documentReference]);

  const markMessagesAsRead = async () => {
    if (!documentData?._id) return;

    try {
      await axios.post("/mark-message-read", {
        documentId: documentData._id,
        userId: user,
        role: role
      });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        message.warning(err.response.data.message || "Error while marking messages as read");
      } else if (err.response && err.response.status === 401) {
        message.error("User not authorized");
      } else {
        message.error("Something went wrong");
      }
    }
  };

  const fetchChatMessages = async () => {
    try {
      const res = await axios.post("fetch-chatSection", {
        projectName: documentData.projectName,
        documentReference: documentData.documentReference,
        category: documentData.category,
        type: documentData.type
      });

      setChatMessages(res.data?.chatMessages || []);
    } catch (err) {
      console.error("Chat fetch failed", err);
      message.error("Failed to fetch chat messages");
    }
  };

  useEffect(() => {
    if (chatVisible && chatMessages.length > 0) {
      markMessagesAsRead();
    }
  }, [chatVisible, chatMessages]);

  const sendChatMessage = async () => {
    if (!chatText.trim()) return;

    try {
      await axios.post("chat-section", {
        projectName: documentData.projectName,
        documentReference: documentData.documentReference,
        category: documentData.category,
        type: documentData.type,
        message: chatText,
        user: user
      });
      setChatText("");
      fetchChatMessages();
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 404) {
        message.warning(err.response.data.message || "Error while sending message");
      } else if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong');
      }
    }
  };

  const fetchDocumentData = async () => {
    try {
      setFetchingDocument(true);
      const response = await axios.post(`get-document-details`, { user, role, projectName, documentReference: location.state.documentReference });

      if (response.data && response.data.document && response.data.document.documentHistory) {
        setDocumentData(response.data.document);
        const documentHistory = response.data.document.documentHistory;
        
        if (documentHistory.length > 0) {
          setSelectedRevisionUrl(documentHistory[documentHistory.length - 1].documentUrl || null);
          setSelectedRevisionIndex(documentHistory.length - 1);
        } else {
          setSelectedRevisionUrl(null);
          setSelectedRevisionIndex(0);
          message.warning("No document history available");
        }
      } else {
        if (location.state) {
          setDocumentData(location.state);
          const documentHistory = location.state.documentHistory || [];
          if (documentHistory.length > 0) {
            setSelectedRevisionUrl(documentHistory[documentHistory.length - 1].documentUrl || null);
            setSelectedRevisionIndex(documentHistory.length - 1);
          }
        } else {
          setSelectedRevisionUrl(null);
          setSelectedRevisionIndex(0);
        }
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      message.error("Failed to fetch document data");
      if (location.state) {
        setDocumentData(location.state);
        const documentHistory = location.state.documentHistory || [];
        if (documentHistory.length > 0) {
          setSelectedRevisionUrl(documentHistory[documentHistory.length - 1].documentUrl || null);
          setSelectedRevisionIndex(documentHistory.length - 1);
        }
      } else {
        setSelectedRevisionUrl(null);
        setSelectedRevisionIndex(0);
      }
    } finally {
      setFetchingDocument(false);
    }
  };

  useEffect(() => {
    fetchDocumentData();
    setUploadModalVisible(false);
    setFileList([]);
  }, [location.pathname]);

  const handleCardClick = (revisionIndex) => {    
    if (documentData && documentData.documentHistory && documentData.documentHistory.length > 0) {
      const urlIndex = Math.min(revisionIndex, documentData.documentHistory.length - 1);
      setSelectedRevisionUrl(documentData.documentHistory[urlIndex].documentUrl || null);
      setSelectedRevisionIndex(urlIndex);
      setRevisionModalVisible(true);
    } else {
      message.warning("No document URL available for this revision");
    }
  };

  const calculateTimeline = (history) => {
    if (!history || history.length === 0) return [];

    const timeline = [];
    let prevDate = new Date(history[0].date || new Date());

    // const calculateCustomDayDifference = (currentDate, previousDate) => {
    //   if (!previousDate || !currentDate) return 0;

    //   const current = new Date(currentDate);
    //   const previous = new Date(previousDate);

    //   const timeDifference = current.getTime() - previous.getTime();
    //   let diffInDays = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    //   if (diffInDays === 0 && current.getDate() !== previous.getDate()) {
    //     diffInDays = 1;
    //   }
    //   return diffInDays;
    // };

    const calculateCustomDayDifference = (currentDate, previousDate) => {
      if (!previousDate || !currentDate) return 0;

      const current = new Date(currentDate);
      const previous = new Date(previousDate);

      // 🔑 Strip time (set both to midnight)
      current.setHours(0, 0, 0, 0);
      previous.setHours(0, 0, 0, 0);

      // 🔑 Date-only difference
      const diffInMs = current - previous;
      const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

      // ✅ Inclusive counting for document timelines
      return diffInDays > 0 ? diffInDays + 1 : 0;

    };


    history.forEach((entry, index) => {
      const entryDate = new Date(entry.date || prevDate);
      const duration = index === 0 ? 0 : calculateCustomDayDifference(entryDate, prevDate);

      let shouldInclude = false;
      let color = "default";
      let userValue = Array.isArray(entry.user) ? entry.user[0] : entry.user || "";
      let sentBy = userValue;

      // ✅ Case 1: DC → Consultant
      if (
        userValue.toLowerCase().includes("document controller") &&
        entry.sentTo?.toLowerCase().includes("consultant")
      ) {
        shouldInclude = true;
        color = "gray";
        sentBy = "Document Controller"; // always show DC
      }

      // ✅ Case 2: Consultant → DC
      else if (
        userValue.toLowerCase().includes("consultant") &&
        entry.sentTo?.toLowerCase().includes("document controller")
      ) {
        shouldInclude = true;
        sentBy = "Consultant";

        const status = (entry.consulWithStatus || entry.status || "").toLowerCase();

        if (status.includes("approved") && status.includes("comment")) {
          color = "orange"; // Approved with comments
        } else if (status.includes("approved")) {
          color = "green";
        } else if (
          status.includes("reject") ||
          status.includes("revise")
        ) {
          color = "red";
        } else {
          color = "gray";
        }
      }

      if (shouldInclude) {
        timeline.push({
          revision: `Rev.${entry.version || index + 1}`,
          transmittalDate: entryDate.toLocaleDateString("en-GB"),
          transmittalTime: entryDate.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          duration,
          color,
          status: entry.consulWithStatus || entry.status || "Sent",
          sentBy,
          sentTo: entry.sentTo,
        });

        prevDate = entryDate;
      }
    });

    return timeline;
  };

  useEffect(() => {
    if (documentData?.documentHistory) {
      const timeline = calculateTimeline(documentData.documentHistory); 
      setTimelineData(timeline);
    }
  }, [documentData]);

  const ViewDocumentWorkflow = async () => {
    setLoading(true);
    setIsModalVisible(true);
    try {
      const res = await axios.post("get-document-workflow", {
        documentType: documentData.type,
        documentCategory: documentData.category,
        stage: documentData.stage,
        projectName: projectName,
      });
      if (res && res.data && res.data.workflow) {
        setWorkflow(res.data.workflow);
        setNodes(res.data.workflow.nodes || []);
        setEdges(res.data.workflow.edges || []);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 404) {
        message.warning(err.response.data.message || "No workflow available for this parameter");
      } else if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong');
      }
    }
  };

  const uploadSupportingDocument = async () => {
    if (fileList.length === 0) {
      message.warning("Please upload a file.");
      return;
    }
    if (!title.trim()) {
      message.warning("Please enter a title for the document.");
      return;
    }
    setLoading(true);

    const formData = new FormData();
    formData.append('file', fileList[0].originFileObj);
    formData.append('code', documentData.code);
    formData.append('documentReference', documentData.documentReference);
    formData.append('title', title);

    try {
      const res = await axios.post("supporting-document", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res && res.data && res.data.document) {
        setDocumentData(res.data.document);
      } else if (res && res.data && res.data.supportingDocumentUrl) {
        setDocumentData((prev) => ({
          ...prev,
          supportingDocuments: [...(prev.supportingDocuments || []), res.data.supportingDocumentUrl],
        }));
      }
      message.success("File uploaded successfully");
      setFileList([]);
      setTitle('');
      setUploadModalVisible(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong');
      }
    }
  };

  const handleEngineerCommentSubmit = async () => {
    if (!engineerComment.trim()) {
      message.warning("Please enter a comment");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("engineer-comment", {
        code: documentData.code,
        documentReference: documentData.documentReference,
        comment: engineerComment,
      });

      if (res && res.data && res.data.document) {
        setDocumentData(res.data.document);
        message.success("Comment submitted successfully");
        setEngCommentModalVisible(false);
        setEngineerComment('');
      } else {
        await fetchDocumentData();
        message.success("Comment submitted successfully");
        setEngCommentModalVisible(false);
        setEngineerComment('');
      }
    } catch (err) {
      message.error("Failed to submit comment", err.response?.data || err.message);
      console.error('Error submitting comment:', err);
    } finally {
      setLoading(false);
    }
  };

  const supportingMenu = (
    <Menu onClick={() => setUploadModalVisible(true)}>
      <Menu.Item key="upload" icon={<UploadOutlined />}>
        Upload
      </Menu.Item>
    </Menu>
  );

  const onNodesChange = (changes) => setNodes((nds) => applyNodeChanges(changes, nds));
  const onEdgesChange = (changes) => setEdges((eds) => applyEdgeChanges(changes, eds));

  if (fetchingDocument) {
    return (
      <div className="p-4 sm:p-6 min-h-screen flex justify-center items-center">
        <Card className="w-full max-w-6xl shadow-lg rounded-lg p-8 text-center">
          <Text>Loading document details...</Text>
        </Card>
      </div>
    );
  }


  return (
    <div className="p-4 sm:p-6 min-h-screen flex justify-center">
      {documentData ? (
        <Card className="w-full max-w-6xl shadow-lg rounded-lg">
          <div className="flex justify-between items-center relative w-full mb-4 p-2">
            <Button
              icon={<ArrowLeftOutlined className="text-3xl" />}
              onClick={() => navigate(-1)}
              className="left-0 text-black-600 text-lg hover:text-blue-800"
            />

            <Title level={2}>Document Details</Title>

            <div className="flex gap-2">
              {/* {Array.isArray(responseDocuments) && responseDocuments.length > 0 && (
                <Button
                  type="text"
                  icon={<SendOutlined style={{ color: "#ff4d4f", fontSize: "18px" }} />}
                  title="View Response Documents"
                  onClick={() => setResponseModalVisible(true)}
                />
              )} */}

              <Badge count={unreadCount} size="small" overflowCount={99}>
                <Button
                  type="text"
                  icon={
                    <MessageFilled
                      style={{
                        fontSize: "20px",
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        color: "#fff",
                        borderRadius: "50%",
                        padding: "8px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                      }}
                    />
                  }
                  onClick={async () => {
                    setChatVisible(true);
                    await fetchChatMessages();
                    await markMessagesAsRead();
                    setUnreadCount(0);
                  }}
                  title="Open Chat"
                />
              </Badge>

              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item key="view-workflow" onClick={ViewDocumentWorkflow}>
                      View Workflow
                    </Menu.Item>
                    <Menu.Item
                      key="upload-supporting"
                      onClick={() => setUploadModalVisible(true)}
                    >
                      Upload Supporting Document
                    </Menu.Item>
                    {userType === "Consultant" && (
                      <Menu.Item
                        key="engineer-comment"
                        onClick={() => setEngCommentModalVisible(true)}
                      >
                        Consultant Comment
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button
                  icon={<MoreOutlined />}
                  className="text-black-600 hover:text-blue-800"
                  loading={loading}
                />
              </Dropdown>
            </div>
          </div>

          <Modal
            title="Upload Supporting Document"
            open={uploadModalVisible}
            onCancel={() => {
              setUploadModalVisible(false);
            }}
            footer={null}
          >
            <div className="mb-4">
              <label>Enter Title:</label>
              <Input
                placeholder="Enter supporting document title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <Upload
              name="document"
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>

            <div className="mt-4">
              <Button
                type="primary"
                onClick={() => {
                  uploadSupportingDocument();
                }}
                loading={loading}
              >
                Submit Document
              </Button>
            </div>
          </Modal>

          <Modal
            title="View Supporting Document"
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={null}
            width={700}
            bodyStyle={{ maxHeight: "500px", overflowY: "auto" }}
          >
            {selectedDocument ? (
              /\.(jpg|jpeg|png|gif)$/i.test(selectedDocument) ? (
                <img
                  src={selectedDocument}
                  alt="Supporting Document"
                  style={{ maxWidth: "100%", borderRadius: "6px" }}
                />
              ) : (
                <a href={selectedDocument} target="_blank" rel="noopener noreferrer">
                  Open Document
                </a>
              )
            ) : (
              <p>No document selected.</p>
            )}
          </Modal>

          <Modal
            title="Engineer Comment"
            open={engCommentModalVisible}
            onCancel={() => {
              setEngCommentModalVisible(false);
              setEngineerComment('');
            }}
            footer={[
              <Button key="cancel" onClick={() => {
                setEngCommentModalVisible(false);
                setEngineerComment('');
              }}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleEngineerCommentSubmit}
                loading={loading}
              >
                Submit
              </Button>,
            ]}
            width={600}
          >
            <TextArea
              rows={4}
              value={engineerComment}
              onChange={(e) => setEngineerComment(e.target.value)}
              placeholder="Enter your comment here"
              className="mb-4"
            />
          </Modal>

          <Modal
            open={revisionModalVisible}
            onCancel={() => setRevisionModalVisible(false)}
            footer={null}
            width="85vw"
            style={{ top: 30 }}
            bodyStyle={{
              height: "80vh",
              padding: 0,
              overflow: "hidden"
            }}
            destroyOnClose
          >
            {selectedRevisionUrl ? (
              /\.(jpg|jpeg|png|gif)$/i.test(selectedRevisionUrl) ? (
                <img
                  src={selectedRevisionUrl}
                  alt={`Revision Document - Version ${selectedRevisionIndex + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    background: "#fff"
                  }}
                />
              ) : (
                <iframe
                  src={selectedRevisionUrl}
                  title="Revision Document"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              )
            ) : (
              <div className="h-full flex items-center justify-center">
                <Text type="secondary">
                  No document available for this revision
                </Text>
              </div>
            )}
          </Modal>       
          
          {/* document chronology */}
          <Row gutter={[16, 16]} align="top">

               {/* {documentData?.respondingTo ? (
                <Col xs={24} sm={12} md={8}>
                  <Card
                    title="Base Document Details"
                    bordered
                    className="bg-gray-50 h-full"
                  >
                    <Text><strong>Project Name:</strong> {baseDocument?.projectName || "N/A"}</Text><br />
                    <Text><strong>Reference:</strong> {baseDocument?.documentReference || "N/A"}</Text><br />
                    <Text><strong>Category:</strong> {baseDocument?.category || "N/A"}</Text><br />
                    <Text><strong>Type:</strong> {baseDocument?.type || "N/A"}</Text><br />
                    <Text><strong>Status:</strong>
                      <Tag className="ml-2" color={getStatusColor(baseDocument?.status)}>
                        {baseDocument?.status || "N/A"}
                      </Tag>
                    </Text><br />
                    <Text><strong>Revision:</strong> {baseDocument?.version || baseDocument?.revision || "N/A"}</Text><br />
                    <Text><strong>Uploaded By:</strong> {baseDocument?.uploadedBy || "N/A"}</Text><br />
                    <Text><strong>Date:</strong> {
                      baseDocument?.uploadedDate
                        ? new Date(baseDocument.uploadedDate).toLocaleDateString("en-GB")
                        : "N/A"
                    }</Text><br />

                    {baseDocument?.documentUrl?.length > 0 && (
                      <div className="mt-3">
                        <Button
                          type="link"
                          onClick={() => {
                            setBaseDocUrl(baseDocument.documentUrl.at(-1));
                            setBaseDocModalOpen(true);
                          }}
                        >
                          View Base Document
                        </Button>
                      </div>
                    )}
                  </Card>
                </Col>
              ) : ( */}

            {/* { */}
            {/* // userType !== "Consultant" ? ( */}
              <Col xs={24} sm={12} md={8}>
                <Card
                  title={
                    <div className="flex items-center justify-between">
                      <span>Document Chronology & Revision History ({documentData?.documentHistory?.length} cards)</span>
                      <Badge count={documentData?.documentHistory?.length || 0} showZero color="blue" />
                    </div>
                  }
                  bordered
                  className="bg-gray-50 h-full"
                >
                  <div className="max-h-[450px] overflow-y-auto pr-2">
                    {documentData?.documentHistory && documentData.documentHistory.length > 0 ? (
                      [...documentData.documentHistory].reverse().map((entry, index) => {
                        const actualIndex = documentData.documentHistory.length - 1 - index;
                        if (entry.uploadedBy) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Uploaded By:</strong> {entry.uploadedBy || "N/A"}</Text><br />
                              <Text><strong>Status:</strong> {entry.status || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        }
                        else if (entry.rejectionReason) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.rejectionReason.version}</Text>
                              </div>
                              <Text><strong>Status:</strong> {entry.status || "N/A"}</Text><br />
                              <div className="mt-2 p-2 bg-gray-100 rounded">
                                <Text className="text-sm"><strong>Reason:</strong> {entry.rejectionReason.reason || "N/A"}</Text><br />
                                <Text><strong>Rejected By:</strong> {entry.rejectionReason.rejectedBy || "N/A"}</Text><br />
                                <Text><strong>Date:</strong> {entry.rejectionReason.date ? new Date(entry.rejectionReason.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                                <Text><strong>Time:</strong> {entry.rejectionReason.date ? new Date(entry.rejectionReason.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text>
                              </div>
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        }
                        else if (entry.action === "Rejected") {
                          return (
                            <Card
                              key={`history-${entry.action}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-red-50 hover:bg-red-100 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">
                                  Rev {entry.version || "N/A"}
                                </Text>
                              </div>

                              <Text>
                                <strong>Status:</strong> Rejected
                              </Text>
                              <br />

                              <div className="mt-2 p-2 bg-white rounded border">
                                <Text className="text-sm">
                                  <strong>Reason:</strong> {entry.reason || "N/A"}
                                </Text>
                                <br />

                                <Text>
                                  <strong>Rejected By:</strong> {entry.user || "N/A"}
                                </Text>
                                <br />

                                <Text>
                                  <strong>Role:</strong> {entry.role || "N/A"}
                                </Text>
                                <br />

                                <Text>
                                                  <Text><strong>Comment:</strong> {entry.reason || "N/A"}</Text>
              <strong>Date:</strong>{" "}
                                  {entry.date
                                    ? new Date(entry.date).toLocaleDateString("en-GB")
                                    : "N/A"}
                                </Text>
                                <br />

                                <Text>
                                  <strong>Time:</strong>{" "}
                                  {entry.date
                                    ? new Date(entry.date).toLocaleTimeString("en-GB", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "N/A"}
                                </Text>
                              </div>

                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">
                                  Click to view document version
                                </Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        }
                        else if (entry.approvedBy) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Action By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Approved By:</strong> {entry.approvedBy || "N/A"}</Text><br />
                              <Text><strong>Comment:</strong> {entry.reason || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo && entry.DocWithTransmittal) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-gray-200 hover:bg-gray-300 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo && entry.consulWithStatus) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-gray-200 hover:bg-gray-300 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Status:</strong> {entry.consulWithStatus || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } 
                        return null;
                      })
                    ) : (
                      <Empty
                        description="No revision history available"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                </Card>
              </Col>
            {/* // ) :  (
            //   documentData?.type === 'Construction (CNS)' &&  documentData.category === 'Correspondences (LTR)' && (
            // } */}

              {userType === "Consultant" &&
                documentData?.type === "Construction (CNS)" &&
                documentData?.category === "Correspondences (LTR)" && (
                <Col xs={24} sm={12} md={8}>
                <Card
                  title={
                    <div className="flex items-center justify-between">
                      <span>Document Chronology & Revision History ({documentData?.documentHistory?.length} cards)</span>
                      <Badge count={documentData?.documentHistory?.length || 0} showZero color="blue" />
                    </div>
                  }
                  bordered
                  className="bg-gray-50 h-full"
                >
                  <div className="max-h-[450px] overflow-y-auto pr-2">
                    {documentData?.documentHistory && documentData?.documentHistory.length > 0 ? (
                      [...documentData?.documentHistory].reverse().map((entry, index) => {
                        const actualIndex = documentData.documentHistory.length - 1 - index;
                        if (entry.uploadedBy) {
                          return (
                            <Card
                            key={`history-${entry.status}-${index}`}
                            className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                            onClick={() => handleCardClick(actualIndex)}
                          >
                            <Text><strong>Status:</strong> {entry.status}</Text><br />

                            {entry.responseReference && (
                              <Text>
                                <strong>Response Ref:</strong> {entry.responseReference}
                              </Text>
                            )}
                            <br />

                            <Text>
                              <strong>Uploaded By:</strong> {entry.user || entry.uploadedBy || "N/A"}
                            </Text><br />

                            <Text>
                              <strong>Date:</strong>{" "}
                              {entry.date
                                ? new Date(entry.date).toLocaleDateString("en-GB")
                                : "N/A"}
                            </Text><br />

                            <Text>
                              <strong>Time:</strong>{" "}
                              {entry.date
                                ? new Date(entry.date).toLocaleTimeString("en-GB", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "N/A"}
                            </Text>
                            <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                          </Card>
                          );
                        }
                        else if (entry.rejectionReason) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.rejectionReason.version}</Text>
                              </div>
                              <Text><strong>Status:</strong> {entry.status || "N/A"}</Text><br />
                              <div className="mt-2 p-2 bg-gray-100 rounded">
                                <Text className="text-sm"><strong>Reason:</strong> {entry.rejectionReason.reason || "N/A"}</Text><br />
                                <Text><strong>Rejected By:</strong> {entry.rejectionReason.rejectedBy || "N/A"}</Text><br />
                                <Text><strong>Date:</strong> {entry.rejectionReason.date ? new Date(entry.rejectionReason.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                                <Text><strong>Time:</strong> {entry.rejectionReason.date ? new Date(entry.rejectionReason.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text>
                              </div>
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        }
                        else if (entry.approvedBy) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Action By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Approved By:</strong> {entry.approvedBy || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo && entry.DocWithTransmittal) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-gray-200 hover:bg-gray-300 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo && entry.consulWithStatus) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-gray-200 hover:bg-gray-300 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Status:</strong> {entry.consulWithStatus || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } else if (entry.sentTo) {
                          return (
                            <Card
                              key={`history-${entry.status}-${entry.user}-${entry.version}-${actualIndex}`}
                              className="border p-3 mb-3 bg-white hover:bg-blue-50 cursor-pointer transition-colors duration-200 shadow-sm"
                              onClick={() => handleCardClick(actualIndex)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Text className="text-xs text-gray-500">Rev {entry.version}</Text>
                              </div>
                              <Text><strong>Sent By:</strong> {entry.user || "N/A"}</Text><br />
                              <Text><strong>Sent To:</strong> {entry.sentTo || "N/A"}</Text><br />
                              <Text><strong>Date:</strong> {entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "N/A"}</Text><br />
                              <Text><strong>Time:</strong> {entry.date ? new Date(entry.date).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' }) : "N/A"}</Text><br />
                              <div className="mt-2 flex justify-between items-center">
                                <Text className="text-xs text-blue-600">Click to view document version</Text>
                                <EyeOutlined className="text-blue-500" />
                              </div>
                            </Card>
                          );
                        } 
                        return null;
                      })
                    ) : (
                      <Empty
                        description="No revision history available"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                </Card>
              </Col>
// }
            )}
          

            <Col xs={24} sm={8} md={8} className="flex justify-center items-center">
              <div className="bg-white p-2 rounded-lg shadow-md w-full">
                {documentData?.documentUrl && documentData.documentUrl.length > 0 ? (
                  documentData.documentUrl[documentData.documentUrl.length - 1].endsWith(".pdf") ? (
                    <>
                      <embed
                        src={documentData.documentUrl[documentData.documentUrl.length - 1]}
                        type="application/pdf"
                        width="100%"
                        height="450px"
                        className="rounded-lg"
                      />
                      <p className="text-center mt-2">
                        <a href={documentData.documentUrl[documentData.documentUrl.length - 1]} target="_blank" rel="noopener noreferrer">
                          Open PDF
                        </a>
                      </p>
                    </>
                  ) : (
                    <Image
                      src={documentData.documentUrl[documentData.documentUrl.length - 1]}
                      alt="Document Preview"
                      className="rounded-lg w-full"
                      height={450}
                      style={{ objectFit: "contain", backgroundColor: "white" }}
                    />
                  )
                ) : (
                  <Text type="secondary">No document available.</Text>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card bordered className="h-full">
                <Text><strong>Project Name:</strong> {documentData.projectName || "N/A"}</Text><br />
                <Text><strong>Description:</strong> {documentData.documentDescription || "N/A"}</Text><br />
                <Text><strong>Reference:</strong> {documentData.documentReference || "N/A"}</Text><br />
                <Text><strong>Category:</strong> {documentData.category || "N/A"}</Text><br />
                <Text><strong>Type:</strong> {documentData.type || "N/A"}</Text><br />
                <Text><strong>Uploaded Date:</strong> {documentData.uploadedDate ? new Date(documentData.uploadedDate).toLocaleDateString() : "N/A"}</Text><br />
                <Text><strong>Current Status:</strong> 
                  <Tag color={getStatusColor(documentData.status)} className="ml-2">
                    {documentData.status || "N/A"}
                  </Tag>
                </Text><br />
                <Text><strong>Current Revision:</strong> {documentData.version || documentData.revision || "1"}</Text><br />
                <Text><strong>File Size:</strong> {documentData.fileSize ? documentData.fileSize : "N/A"}</Text><br />
              </Card>

              <Card bordered className="h-full">
                <Text strong>Supporting Documents</Text>
                <br /><br />
                {Array.isArray(documentData?.supportingDocuments) && documentData.supportingDocuments.length > 0 ? (
                  <List
                    dataSource={documentData.supportingDocuments}
                    renderItem={(item, index) => (
                      <List.Item key={index} style={{ padding: '8px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text strong>{index + 1}.</Text>
                          <a
                            onClick={() => {
                              setSelectedDocument(item.url);
                              setViewModalVisible(true);
                            }}
                          >
                            {item.title}
                          </a>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty 
                    description="No supporting documents available" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Card>
            </Col>
          </Row>
       
          {/* timeline details */}
          {timelineData.length > 0 && (
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <div className="mb-4 text-center">
                <Text strong>Document Timeline</Text>
                <br />
                <Text>Total Duration: {timelineData.reduce((sum, data) => sum + Number(data.duration), 0)} days</Text>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-4">
                {timelineData.map((data, index) => (
                  <React.Fragment key={index}>
                    <div
                      className={`w-28 h-28 flex flex-col items-center justify-center rounded-lg border-4 shadow-md ${
                        data.color === 'blue'
                          ? 'border-blue-500 bg-blue-50'
                          : data.color === 'orange'
                          ? 'border-orange-500 bg-orange-50'
                          : data.color === 'green'
                          ? 'border-green-500 bg-green-50'
                          : data.color === 'red'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-500 bg-gray-50'
                      }`}
                    >
                      <Text strong className="text-sm text-center">{data.revision}</Text>
                      <Text className="text-xs text-gray-600 text-center">{data.transmittalDate}</Text>
                      <Text className="text-xs text-gray-600 text-center">{data.transmittalTime}</Text>
                      {/* {data.status && data.status !== 'Uploaded' && (
                        <Text className="text-xs text-center font-medium mt-1">
                          {data.status}
                        </Text>
                      )} */}
                    </div>
                    
                    {index < timelineData.length - 1 && (
                      <div className="flex flex-col items-center">
                        <div
                          className="h-1 w-16"
                          style={{
                            backgroundColor: data.color === 'blue' ? '#1890ff' 
                              : data.color === 'orange' ? '#fa8c16'
                              : data.color === 'green' ? '#52c41a'
                              : data.color === 'red' ? '#ff4d4f' 
                              : '#d9d9d9',
                          }}
                        ></div>
                        <Text className="text-xs mt-1 text-center">
                          {timelineData[index + 1]?.duration || 0} days
                        </Text>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              {/* Legend */}
              <div className="flex justify-center flex-wrap gap-4 mt-6">
                {/* <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                  <Text className="text-sm">Uploaded</Text>
                </div> */}
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-gray-500 mr-2"></span>
                  <Text className="text-sm">Transmitted</Text>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-orange-500 mr-2"></span>
                  <Text className="text-sm">Approved with Comments</Text>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                  <Text className="text-sm">Approved</Text>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                  <Text className="text-sm">Rejected/Revise & Resubmit</Text>
                </div>
              </div>
            </div>
          )}

          {/* engineer comments */}
          <div className="mt-4">
            <Card bordered className="bg-white">
              <Text strong>Consultant Comments</Text>
              <br /><br />
              {Array.isArray(documentData?.engineerComments) && documentData.engineerComments.length > 0 ? (
                <List
                  dataSource={documentData.engineerComments}
                  renderItem={(item, index) => (
                    <List.Item key={index} style={{ padding: '8px 0' }}>
                      <div>
                        <Text strong>Rev {item.revision}: </Text>
                        <Text>{item.comment}</Text>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  description="No engineer comments available"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>
          </div>
        </Card>
      ) : (
        <Empty description="No document data available" />
      )}

      <Modal
        title={`Workflow: ${Array.isArray(workflow?.documentType) ? workflow.documentType.join(", ") : workflow?.documentType || "Document Workflow"}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)} style={{ backgroundColor: "red", color: "white" }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {workflow ? (
          <div style={{ height: "500px", background: "#f4f4f4", padding: "10px", borderRadius: "8px" }}>
            <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} fitView>
              <MiniMap />
              <Controls />
              <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
          </div>
        ) : (
          <Empty description="No workflow data available" />
        )}
      </Modal>

      <Modal
        title={`Response Documents (${responseDocuments.length})`}
        open={responseModalVisible}
        onCancel={() => setResponseModalVisible(false)}
        footer={null}
        width={700}
        bodyStyle={{ maxHeight: "500px", overflowY: "auto" }}
      >
        {Array.isArray(responseDocuments) && responseDocuments.length > 0 ? (
          <List
            dataSource={responseDocuments}
            renderItem={(resp, index) => (
              <List.Item key={resp._id}>
                <div style={{ width: "100%" }}>
                  <Text strong>Response {index + 1}</Text><br />

                  <Text>
                    <strong>Responded By:</strong> {resp.uploadedBy || "N/A"}
                  </Text><br />

                  <Text>
                    <strong>Date:</strong>{" "}
                    {resp.uploadedDate
                      ? new Date(resp.uploadedDate).toLocaleDateString("en-GB")
                      : "N/A"}
                  </Text><br />

                  <Text>
                    <strong>Status:</strong>{" "}
                    <Tag color="red">{resp.status || "Responded"}</Tag>
                  </Text><br /><br />

                  {Array.isArray(resp.documentUrl) && resp.documentUrl[0] && (
                    <a
                      href={resp.documentUrl[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <EyeOutlined /> View Response Document
                    </a>
                  )}
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Empty description="No response documents available" />
        )}
      </Modal>

      <Drawer
        title={
          <span style={{ 
            fontSize: '16px', 
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            Document Chat
          </span>
        }
        placement="right"
        width={420}
        open={chatVisible}
        onClose={() => setChatVisible(false)}
        styles={{
          header: {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#fafafa'
          }
        }}
      >
        {/* Messages Container */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          padding: '16px 24px',
          background: '#fafafa'
        }}>
          <Alert
            message="Important"
            description="The chats are visible for all users in the workflow. Please keep the chat respectful"
            type="warning"
            showIcon
          />
          {chatMessages.length > 0 ? (
            <List
              dataSource={chatMessages}
              renderItem={(item, index) => (
                <List.Item 
                  key={index}
                  style={{
                    border: 'none',
                    padding: '12px 0',
                    display: 'block'
                  }}
                >
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                    border: '1px solid #f0f0f0'
                  }}>
                    <Text 
                      strong 
                      style={{ 
                        color: '#1a1a1a',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {item.user}
                    </Text>
                    <div style={{ 
                      marginTop: '6px',
                      color: '#262626',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}>
                      {item.message}
                    </div>
                    <Text 
                      type="secondary" 
                      style={{ 
                        fontSize: '12px',
                        color: '#8c8c8c',
                        marginTop: '6px',
                        display: 'block'
                      }}
                    >
                      {new Date(item.createdAt).toLocaleString()}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '300px'
            }}>
              <Empty 
                description={
                  <span style={{ color: '#8c8c8c' }}>
                    No chat messages yet
                  </span>
                }
              />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div style={{
          background: '#ffffff',
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <TextArea
            rows={3}
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            placeholder="Please keep the chat respectful..."
            style={{
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              fontSize: '14px',
              resize: 'none'
            }}
            styles={{
              textarea: {
                '&:focus': {
                  borderColor: '#4096ff',
                  boxShadow: '0 0 0 2px rgba(64, 150, 255, 0.1)'
                }
              }
            }}
          />
          <Button
            type="primary"
            block
            icon={<SendOutlined />}
            onClick={sendChatMessage}
            style={{
              marginTop: '12px',
              height: '40px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '14px',
              background: '#7daa91ff',
              boxShadow: '0 2px 4px rgba(22, 119, 255, 0.2)'
            }}
          >
            Send Message
          </Button>
        </div>
      </Drawer>

      <Modal
        open={baseDocModalOpen}
        onCancel={() => {
          setBaseDocModalOpen(false);
          setBaseDocUrl(null);
        }}
        footer={null}
        width="80%"
        destroyOnClose
        title="Base Document Preview"
      >
        {baseDocUrl ? (
          baseDocUrl.endsWith(".pdf") ? (
            <embed
              src={baseDocUrl}
              type="application/pdf"
              width="100%"
              height="600px"
            />
          ) : (
            <Image
              src={baseDocUrl}
              alt="Base Document"
              style={{ maxHeight: "600px", objectFit: "contain" }}
            />
          )
        ) : (
          <Text>No document available</Text>
        )}
      </Modal>

    </div>
  );
};

export default DocumentDetails;